import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import ScrollableSection from '@travel/traveler-core/components/ScrollableSection';
import useCurrencyChangeCallback from '@travel/traveler-core/hooks/useCurrencyChangeCallback';
import useDeviceType, {
  DESKTOP_SCREEN,
  MOBILE_SCREEN,
} from '@travel/traveler-core/hooks/useDeviceType';
import { cx, isEmptyArray, isNotEmptyArray } from '@travel/utils';

import ContentsCarousel from 'components/ContentsCarousel';
import Media from 'components/Media';
import WithSkeletonLoading from 'components/WithSkeletonLoading';

import { getLocation } from 'store/__router/selectors';
import { fetchAreaDetails } from 'store/areaDetails/actions';
import { getAreaName, getIsFetching, getRecommendedProviders } from 'store/areaDetails/selectors';

import type { SalesPromotion } from 'AreaDetails-Types';
import { Translate } from 'core/translate';
import { Link } from 'core/universalRouter/Link';
import paths from 'core/universalRouter/paths';

import styles from './recommendedProviders.module.scss';

type TileProps = {
  providerName: string;
  salesPromotion?: SalesPromotion;
  totalReview?: number;
  reviewScore?: number | null;
};

type Props = {
  pathId: string;
};

/* 365.33 + 16 */
const PC_IMAGE_WIDTH = 381.33;

function Tile(props: TileProps) {
  const { salesPromotion } = props;

  const hasCoupon =
    salesPromotion?.offerType === 'COUPON' && Boolean(salesPromotion?.incentive?.value);
  const reviewScoreMax = 5;

  return (
    <>
      {hasCoupon && salesPromotion?.representativeFeatureName && (
        <div className={styles.ribbon} data-testid="recommendedProviders-tile-coupon">
          <div data-testid="recommendedProviders-representative">
            {salesPromotion.representativeFeatureName}
          </div>
          <div className={styles.shadow} />
        </div>
      )}
      <div className={styles.infoContainer} data-testid="recommendedProviders-tile-content">
        <div className={styles.info}>
          <div className={styles.providerName} data-testid="recommendedProviders-providerName">
            {props.providerName}
          </div>
          <div className={styles.review}>
            {Number(props.reviewScore) > 0 &&
              Number(props.totalReview) > 0 &&
              `${props.reviewScore}/${reviewScoreMax} (${props.totalReview})`}
          </div>
        </div>
      </div>
    </>
  );
}

function RecommendedProviders(props: Props) {
  const location = useSelector(getLocation);
  const items = useSelector(getRecommendedProviders);
  const areaName = useSelector(getAreaName);
  const isFetching = useSelector(getIsFetching);
  const isMobile = useDeviceType() === MOBILE_SCREEN;
  const isPC = useDeviceType() === DESKTOP_SCREEN;

  const dispatch = useDispatch();

  const onCurrencyChange = useCallback(
    () => dispatch(fetchAreaDetails({ pathId: props.pathId })),
    [dispatch, props.pathId],
  );
  useCurrencyChangeCallback(onCurrencyChange);

  if (isEmptyArray(items) && !isFetching) {
    return null;
  }

  const content =
    isNotEmptyArray(items) &&
    items.map((item, index) => {
      const linkPath = `${paths.providerInfo.pathResolver(item.pathId)}${location?.search || ''}`;
      const isDisableLazyload = !(isMobile ? index > 1 : index > 3);

      return (
        <article
          className={styles.listItem}
          key={item.id}
          data-testid={`RecommendedProviders-listItem-article-${index}`}
        >
          <Link to={linkPath}>
            <div className={cx(styles.imageWrapper, styles.imageSize)}>
              <Media
                media={item.media?.[0]}
                wrapperClassName={styles.mediaWrapper}
                className={styles.media}
                sizes={isMobile ? '100vw' : '30vw'}
                isDisableLazyload={isDisableLazyload}
              />
              <Tile
                providerName={item.providerName}
                salesPromotion={item.price?.salesPromotion}
                totalReview={item.totalReviews}
                reviewScore={item.reviewScore}
              />
            </div>
          </Link>
        </article>
      );
    });

  return (
    <section data-testid="recommendedProviders-wrapper" className={styles.recommendedProviders}>
      <h2>
        <Translate
          id="AreaTop.Recommended_Hotels"
          data={{ area_name: areaName || '-' }}
          className={styles.header}
        />
      </h2>
      <WithSkeletonLoading
        count={4}
        row={1}
        isLoading={isFetching}
        customStyle="textWithImageColumn"
        skeletonItemClassName={styles.loader}
        skeletonImageClassName={styles.imageSize}
        direction="row"
      >
        {!isPC ? (
          <ScrollableSection className={styles.list} childClassName={styles.scrollableSectionItem}>
            {content}
          </ScrollableSection>
        ) : (
          <ContentsCarousel
            fixedCardWidth={PC_IMAGE_WIDTH}
            className={cx(styles.carousel, styles.list)}
            navButtonClassName={styles.carouselButton}
            displayCount={3}
          >
            {content}
          </ContentsCarousel>
        )}
      </WithSkeletonLoading>
    </section>
  );
}

export default RecommendedProviders;
